// AdminVisites.jsx
import { db } from "@/Firebase";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import styles from "./AdminVisites.module.css";

const STATUTS = {
  en_attente: { label: "En attente",  color: "#92400e", bg: "#fef3c7", dot: "#f59e0b" },
  confirmé:   { label: "Confirmée",   color: "#065f46", bg: "#d1fae5", dot: "#10b981" },
  annulé:     { label: "Annulée",     color: "#991b1b", bg: "#fee2e2", dot: "#ef4444" },
};

const formatDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("fr-DZ", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const formatPrice = (price) => {
  if (!price) return null;
  return Number(price).toLocaleString("fr-DZ") + " DA";
};

const AdminVisites = () => {
  const [visites, setVisites]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filterStatut, setFilter]   = useState("tous");
  const [search, setSearch]         = useState("");
  const [updating, setUpdating]     = useState(null);
  const [selected, setSelected]     = useState(null); // détail popup

  const fetchVisites = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "visites"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setVisites(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Erreur chargement visites:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVisites(); }, []);

  const handleStatut = async (visiteId, newStatut) => {
    setUpdating(visiteId);
    try {
      await updateDoc(doc(db, "visites", visiteId), { statut: newStatut });
      setVisites((prev) =>
        prev.map((v) => (v.id === visiteId ? { ...v, statut: newStatut } : v))
      );
      if (selected?.id === visiteId) setSelected((s) => ({ ...s, statut: newStatut }));
    } catch (e) {
      console.error(e);
    } finally {
      setUpdating(null);
    }
  };

  const filtered = useMemo(() => {
    let list = visites;
    if (filterStatut !== "tous") list = list.filter((v) => v.statut === filterStatut);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(
        (v) =>
          v.nom?.toLowerCase().includes(s) ||
          v.email?.toLowerCase().includes(s) ||
          v.telephone?.includes(s) ||
          v.propertyName?.toLowerCase().includes(s) ||
          v.propertyLocation?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [visites, filterStatut, search]);

  const stats = useMemo(() => ({
    tous:      visites.length,
    en_attente: visites.filter((v) => v.statut === "en_attente").length,
    confirmé:  visites.filter((v) => v.statut === "confirmé").length,
    annulé:    visites.filter((v) => v.statut === "annulé").length,
  }), [visites]);

  return (
    <div className={styles.page}>
      {/* ── Sidebar ── */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarLogo}>
          <span>🏠</span>
          <span>Admin</span>
        </div>
        <nav className={styles.sidebarNav}>
          <div className={`${styles.navItem} ${styles.navActive}`}>
            <span>📋</span> Demandes de visite
          </div>
        </nav>
        <div className={styles.sidebarStats}>
          {[
            { key: "tous",       label: "Total",       icon: "📊" },
            { key: "en_attente", label: "En attente",  icon: "⏳" },
            { key: "confirmé",   label: "Confirmées",  icon: "✅" },
            { key: "annulé",     label: "Annulées",    icon: "❌" },
          ].map(({ key, label, icon }) => (
            <div
              key={key}
              className={`${styles.statCard} ${filterStatut === key ? styles.statActive : ""}`}
              onClick={() => setFilter(key)}
            >
              <span className={styles.statIcon}>{icon}</span>
              <div>
                <div className={styles.statNum}>{stats[key] ?? 0}</div>
                <div className={styles.statLabel}>{label}</div>
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* ── Main ── */}
      <main className={styles.main}>
        <header className={styles.topBar}>
          <div>
            <h1 className={styles.pageTitle}>Demandes de visite</h1>
            <p className={styles.pageSubtitle}>
              {filtered.length} résultat{filtered.length !== 1 ? "s" : ""}
              {filterStatut !== "tous" ? ` · ${STATUTS[filterStatut]?.label}` : ""}
            </p>
          </div>
          <div className={styles.topBarActions}>
            <input
              className={styles.searchInput}
              type="text"
              placeholder="🔍  Rechercher…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className={styles.refreshBtn} onClick={fetchVisites} disabled={loading}>
              {loading ? "…" : "↻ Actualiser"}
            </button>
          </div>
        </header>

        {/* ── Filtre pills ── */}
        <div className={styles.filterRow}>
          {[
            { key: "tous",       label: "Tous" },
            { key: "en_attente", label: "⏳ En attente" },
            { key: "confirmé",   label: "✅ Confirmées" },
            { key: "annulé",     label: "❌ Annulées" },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`${styles.pill} ${filterStatut === key ? styles.pillActive : ""}`}
              onClick={() => setFilter(key)}
            >
              {label}
              <span className={styles.pillCount}>{stats[key] ?? 0}</span>
            </button>
          ))}
        </div>

        {/* ── Table ── */}
        {loading ? (
          <div className={styles.loadingBox}>
            <div className={styles.spinner} />
            <span>Chargement des demandes…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.emptyBox}>
            <span style={{ fontSize: 48 }}>📭</span>
            <p>Aucune demande trouvée.</p>
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Contact</th>
                  <th>Bien</th>
                  <th>Prix</th>
                  <th>Date</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => {
                  const st = STATUTS[v.statut] || STATUTS.en_attente;
                  return (
                    <tr
                      key={v.id}
                      className={styles.tableRow}
                      onClick={() => setSelected(v)}
                    >
                      <td>
                        <div className={styles.clientCell}>
                          <div className={styles.avatar}>
                            {v.nom?.[0]?.toUpperCase() || "?"}
                          </div>
                          <span className={styles.clientName}>{v.nom || "—"}</span>
                        </div>
                      </td>
                      <td>
                        <div className={styles.contactCell}>
                          <span>📞 {v.telephone || "—"}</span>
                          <span>✉️ {v.email || "—"}</span>
                        </div>
                      </td>
                      <td>
                        <div className={styles.propertyCell}>
                          <span className={styles.propertyName}>
                            {v.propertyName || "—"}
                          </span>
                          {v.propertyLocation && (
                            <span className={styles.propertyLoc}>
                              📍 {v.propertyLocation}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={styles.priceCell}>
                        {formatPrice(v.propertyPrice) || "—"}
                      </td>
                      <td className={styles.dateCell}>{formatDate(v.createdAt)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <select
                          className={styles.statutSelect}
                          style={{ background: st.bg, color: st.color }}
                          value={v.statut}
                          disabled={updating === v.id}
                          onChange={(e) => handleStatut(v.id, e.target.value)}
                        >
                          <option value="en_attente">⏳ En attente</option>
                          <option value="confirmé">✅ Confirmée</option>
                          <option value="annulé">❌ Annulée</option>
                        </select>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className={styles.actionBtns}>
                          <a
                            href={`https://wa.me/${v.telephone?.replace(/\D/g, "")}?text=${encodeURIComponent(`Bonjour ${v.nom}, suite à votre demande de visite pour "${v.propertyName}"…`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.actionWa}
                            title="Contacter sur WhatsApp"
                          >
                            <img
                              src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                              alt="WA"
                              style={{ width: 18 }}
                            />
                          </a>
                          {v.propertyUrl && (
                            <a
                              href={v.propertyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.actionView}
                              title="Voir l'annonce"
                            >
                              🔗
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* ── Detail Modal ── */}
      {selected && (
        <div className={styles.detailOverlay} onClick={() => setSelected(null)}>
          <div className={styles.detailPanel} onClick={(e) => e.stopPropagation()}>
            <button className={styles.detailClose} onClick={() => setSelected(null)}>✕</button>
            <h2 className={styles.detailTitle}>Détail de la demande</h2>

            <div className={styles.detailAvatar}>
              {selected.nom?.[0]?.toUpperCase() || "?"}
            </div>
            <div className={styles.detailName}>{selected.nom}</div>

            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailKey}>📞 Téléphone</span>
                <span className={styles.detailVal}>{selected.telephone}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailKey}>✉️ Email</span>
                <span className={styles.detailVal}>{selected.email}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailKey}>🏠 Bien</span>
                <span className={styles.detailVal}>{selected.propertyName}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailKey}>📍 Localisation</span>
                <span className={styles.detailVal}>{selected.propertyLocation || "—"}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailKey}>💰 Prix</span>
                <span className={styles.detailVal}>{formatPrice(selected.propertyPrice) || "—"}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailKey}>📅 Date demande</span>
                <span className={styles.detailVal}>{formatDate(selected.createdAt)}</span>
              </div>
            </div>

            <div className={styles.detailStatutRow}>
              <span>Statut :</span>
              <select
                className={styles.statutSelect}
                style={{
                  background: STATUTS[selected.statut]?.bg,
                  color: STATUTS[selected.statut]?.color,
                }}
                value={selected.statut}
                disabled={updating === selected.id}
                onChange={(e) => handleStatut(selected.id, e.target.value)}
              >
                <option value="en_attente">⏳ En attente</option>
                <option value="confirmé">✅ Confirmée</option>
                <option value="annulé">❌ Annulée</option>
              </select>
            </div>

            <div className={styles.detailActions}>
              <a
                href={`https://wa.me/${selected.telephone?.replace(/\D/g, "")}?text=${encodeURIComponent(`Bonjour ${selected.nom}, suite à votre demande de visite pour "${selected.propertyName}"…`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.detailWaBtn}
              >
                <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="" style={{ width: 20 }} />
                Contacter sur WhatsApp
              </a>
              {selected.propertyUrl && (
                <a
                  href={selected.propertyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.detailLinkBtn}
                >
                  🔗 Voir l'annonce
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVisites;
